"use client";

import { Suspense, useCallback, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import Who from "../../Who/Who";
import { useHeroAnimation } from "./hero.func";
import styles from "./Hero.module.css";

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const smoothstep = (value) => {
  const progress = clamp01(value);
  return progress * progress * (3 - 2 * progress);
};

function ProductBottle({ progressRef }) {
  const bottleRef = useRef(null);
  const labelTexture = useTexture("/model/bottle-label.svg");

  const profile = useMemo(
    () => [
      new THREE.Vector2(0.95, -3.05),
      new THREE.Vector2(1.23, -2.98),
      new THREE.Vector2(1.38, -2.72),
      new THREE.Vector2(1.43, -2.35),
      new THREE.Vector2(1.43, 1.45),
      new THREE.Vector2(1.39, 1.72),
      new THREE.Vector2(1.28, 1.94),
      new THREE.Vector2(1.03, 2.18),
      new THREE.Vector2(0.78, 2.25),
      new THREE.Vector2(0.76, 2.65),
      new THREE.Vector2(0.68, 2.71),
      new THREE.Vector2(0.01, 2.71),
    ],
    [],
  );

  useMemo(() => {
    labelTexture.colorSpace = THREE.SRGBColorSpace;
    labelTexture.anisotropy = 4;
    labelTexture.needsUpdate = true;
  }, [labelTexture]);

  useFrame(() => {
    if (!bottleRef.current) return;

    const progress = progressRef.current;
    const rise = smoothstep(progress / 0.72);
    const settle = smoothstep((progress - 0.48) / 0.34);

    bottleRef.current.position.y = THREE.MathUtils.lerp(-7.05, -0.18, rise);
    bottleRef.current.rotation.x = THREE.MathUtils.lerp(-0.08, 0, settle);
    bottleRef.current.rotation.y = THREE.MathUtils.lerp(-0.62, 0.08, settle);
    bottleRef.current.rotation.z = THREE.MathUtils.lerp(-0.2, 0, settle);

    const scale = THREE.MathUtils.lerp(0.54, 0.6, rise);
    bottleRef.current.scale.setScalar(scale);
  });

  return (
    <group ref={bottleRef}>
      <mesh>
        <latheGeometry args={[profile, 64]} />
        <meshPhysicalMaterial
          color="#050505"
          roughness={0.18}
          metalness={0.08}
          clearcoat={1}
          clearcoatRoughness={0.12}
        />
      </mesh>

      <mesh position={[0, -0.28, 0]}>
        <cylinderGeometry args={[1.447, 1.447, 2.42, 64, 1, true]} />
        <meshStandardMaterial
          map={labelTexture}
          roughness={0.3}
          metalness={0.04}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh position={[0, 2.52, 0]}>
        <cylinderGeometry args={[0.79, 0.79, 0.34, 48]} />
        <meshPhysicalMaterial color="#050505" roughness={0.2} clearcoat={0.8} />
      </mesh>

      <mesh position={[0, 3.05, 0]}>
        <cylinderGeometry args={[1.02, 0.94, 0.78, 64]} />
        <meshPhysicalMaterial
          color="#820000"
          roughness={0.28}
          metalness={0.12}
          clearcoat={0.75}
        />
      </mesh>

      <mesh position={[0, 2.7, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.91, 0.055, 12, 64]} />
        <meshStandardMaterial color="#260000" roughness={0.34} />
      </mesh>
      <mesh position={[0, 3.42, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.96, 0.045, 12, 64]} />
        <meshStandardMaterial color="#c10b0b" roughness={0.26} />
      </mesh>
    </group>
  );
}

useTexture.preload("/model/bottle-label.svg");

export default function Hero() {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const scanRef = useRef(null);
  const bottleProgressRef = useRef(0);
  const invalidateRef = useRef(null);

  const updateBottle = useCallback((progress) => {
    bottleProgressRef.current = progress;
    invalidateRef.current?.();
  }, []);

  useHeroAnimation({
    containerRef,
    stageRef,
    frameRef,
    scanRef,
    onProgress: updateBottle,
  });

  return (
    <div id="animation-container" ref={containerRef} className={styles.hero}>
      <div ref={stageRef} className={`animation-container ${styles.stage}`}>
        <div className={styles.bottleCanvas}>
          <Canvas
            frameloop="demand"
            dpr={[1, 1.5]}
            camera={{ position: [0, 0, 13], fov: 35, near: 0.1, far: 40 }}
            gl={{
              antialias: true,
              alpha: true,
              powerPreference: "high-performance",
            }}
            onCreated={({ gl, invalidate }) => {
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 1.18;
              invalidateRef.current = invalidate;
              invalidate();
            }}
          >
            <ambientLight intensity={0.62} />
            <directionalLight position={[4, 7, 7]} intensity={3.3} color="#fff4ee" />
            <pointLight position={[-4, 1, 5]} intensity={22} color="#760000" distance={14} />
            <pointLight position={[4, -2, 4]} intensity={16} color="#ffffff" distance={12} />
            <Suspense fallback={null}>
              <ProductBottle progressRef={bottleProgressRef} />
            </Suspense>
          </Canvas>
        </div>

        <div className={`Hero_Text ${styles.heroText}`}>
          <div ref={frameRef} className={`Heroz ${styles.technicalFrame}`}>
            <div ref={scanRef} className={`scanLight ${styles.scanLight}`} aria-hidden="true" />

            <div className={`barcode ${styles.barcode}`}>
              <img src="/barcode.svg" alt="" />
            </div>

            <div className={`ingredient ${styles.ingredient}`}>
              <p>Ingredients: plant-based · adaptogens · antioxidants · vitamins · minerals · superfood · love and SCIENCE</p>
            </div>

            <div className={`effects ${styles.effects}`}>
              <p>WELLNESS--100%</p>
              <p>ENERGY--92%</p>
              <p>CLARITY--90%</p>
              <p>IMMUNITY--95%</p>
              <p>ARTIFICIAL--00%</p>
            </div>

            <div className={`number1 ${styles.numberOne}`}>
              <h1>#1<span>brand</span></h1>
            </div>

            <div className={`note ${styles.note}`}>
              <p>longevity never expires !</p>
            </div>

            <p className={`toptext ${styles.topText}`}>PREMIUM PLANT SCIENCE, REFINED FOR YOU</p>
          </div>
        </div>
      </div>

      <Who />
    </div>
  );
}
